import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError, reportPdfUrl } from '@/lib/api';
import { toast } from 'sonner';
import { EmptyState, PageHeader, Spinner } from '@/components/Common';
import {
  FileTextIcon,
  DownloadSimpleIcon,
  ArrowRightIcon,
  TrashIcon,
  ShareNetworkIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';

/* GL-SPEC-APP-020 — Reports archive
   Apple-style fluid surface:
   - Soft rounded cards (rounded-2xl), no harsh borders
   - Layered shadow on hover, gentle translateY lift
   - Quiet status pills with translucent fills
   - Live filter bar (search + status), inline delete
   - The "CLIENT SEES THIS" pill makes the active deliverable obvious
*/

const REPORT_KIND_LABEL = {
  walkthrough: 'Walkthrough · Field Audit',
  gap_check: 'Documentation · Gap Check',
  combined: 'Walkthrough + Documentation',
  incident_investigation: 'Incident · Post-Event Review',
};

export default function ReportsPage() {
  const [reports, setReports] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | deactivated
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const { data: engs } = await api.get('/engagements');
      const out = [];
      for (const eng of engs) {
        try {
          const { data: rs } = await api.get(`/engagements/${eng.id}/reports`);
          rs.forEach((r) => out.push({ ...r, eng }));
        } catch (_e) {
          // skip engagements without reports
        }
      }
      out.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      setReports(out);
    } catch (e) {
      toast.error(formatApiError(e));
      setReports([]);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Per-engagement: identify the LATEST active report — that's the one a
  // freshly-copied share link will actually resolve to.
  const latestActiveByEng = useMemo(() => {
    const map = {};
    (reports || []).forEach((r) => {
      if (!r.is_active) return;
      const eid = r.eng?.id;
      if (!eid) return;
      if (!map[eid] || map[eid].created_at < r.created_at) map[eid] = r;
    });
    return map;
  }, [reports]);

  const visible = useMemo(() => {
    if (!reports) return [];
    let rows = reports;
    if (filter === 'active') rows = rows.filter((r) => r.is_active);
    if (filter === 'deactivated') rows = rows.filter((r) => !r.is_active);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.eng?.business_name || '').toLowerCase().includes(q) ||
          (REPORT_KIND_LABEL[r.kind] || '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [reports, filter, search]);

  async function remove(r) {
    if (
      !window.confirm(
        `Delete this report for ${r.eng.business_name}?\n\n` +
          'The engagement itself, findings, photos, and docs are kept. ' +
          'Only this generated PDF and its share link are removed.\n\n' +
          'This cannot be undone.',
      )
    )
      return;
    setBusyId(r.id);
    try {
      await api.delete(`/reports/${r.id}`);
      toast.success('Report deleted.');
      setReports((prev) => (prev || []).filter((x) => x.id !== r.id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  const countAll = (reports || []).length;
  const countActive = (reports || []).filter((r) => r.is_active).length;
  const countDeact = countAll - countActive;

  return (
    <>
      <PageHeader
        eyebrow="Archive"
        title="Reports"
        subtitle="Every generated report across every engagement. Delete superseded versions and see which version your client actually sees."
      />
      <section className="px-5 md:px-8 py-4">
        {/* Filter / search bar — translucent, soft */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--gl-muted)' }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by facility or report type…"
              className="w-full h-11 pl-9 pr-4 text-sm rounded-xl border-0 bg-white/70 backdrop-blur-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2A52A0]/40 transition-all"
              data-testid="reports-search"
            />
          </div>
          <div className="inline-flex rounded-xl bg-white/70 backdrop-blur-md shadow-sm p-1 gap-1" role="tablist">
            {[
              { k: 'all', label: 'All', count: countAll },
              { k: 'active', label: 'Active', count: countActive },
              { k: 'deactivated', label: 'Deactivated', count: countDeact },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`px-4 h-9 text-xs font-semibold rounded-lg transition-all ${
                  filter === f.k
                    ? 'bg-[#142654] text-white shadow-sm'
                    : 'text-neutral-600 hover:bg-white'
                }`}
                style={{ fontFamily: 'Inter', letterSpacing: '0.04em' }}
                data-testid={`reports-filter-${f.k}`}
              >
                {f.label}
                <span className={`ml-1.5 text-[10px] ${filter === f.k ? 'opacity-70' : 'opacity-60'}`}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {reports === null ? (
          <Spinner label="Loading reports…" />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={FileTextIcon}
            title={search || filter !== 'all' ? 'No reports match this filter' : 'No reports yet'}
            body={
              search || filter !== 'all'
                ? 'Try a different search or filter.'
                : 'Generate a report from an engagement to see it here.'
            }
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {visible.map((r) => (
              <ReportCard
                key={r.id}
                r={r}
                isLatestActive={latestActiveByEng[r.eng?.id]?.id === r.id}
                onDelete={() => remove(r)}
                busy={busyId === r.id}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function ReportCard({ r, isLatestActive, onDelete, busy }) {
  const generated = new Date(r.created_at);
  const niceDate = generated.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const niceTime = generated.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  return (
    <div
      className="group relative rounded-2xl bg-white p-5 transition-all duration-200 hover:-translate-y-0.5"
      style={{
        boxShadow:
          '0 1px 2px rgba(17, 24, 39, 0.04), 0 8px 24px rgba(17, 24, 39, 0.06)',
      }}
      data-testid={`report-card-${r.id}`}
    >
      {/* Top status row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-flex items-center text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full"
            style={{ background: '#F4F1E8', color: '#1C2B2B', fontFamily: 'Inter' }}
          >
            {REPORT_KIND_LABEL[r.kind] || r.kind}
          </span>
          {r.is_active ? (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full"
              style={{ background: 'rgba(99,153,34,0.12)', color: '#3F6614', fontFamily: 'Inter' }}
              title="The share link for this report is live and will resolve for clients."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#639922]" /> Active
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full"
              style={{ background: '#F3F4F6', color: '#6B7280', fontFamily: 'Inter' }}
              title="The share link for this report has been deactivated."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" /> Deactivated
            </span>
          )}
          {isLatestActive && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full"
              style={{
                background: 'linear-gradient(135deg, #C9A84C 0%, #B38F30 100%)',
                color: '#1C2B2B',
                fontFamily: 'Inter',
              }}
              title="This is the most recent active report for this engagement — what your client sees when they open the share link you copy."
              data-testid={`report-client-sees-${r.id}`}
            >
              <ShareNetworkIcon size={11} weight="bold" /> Client sees this
            </span>
          )}
        </div>
        {/* Quiet delete affordance — reveals on card hover */}
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-2 rounded-full hover:bg-red-50 active:scale-95 transition-transform disabled:opacity-50"
          style={{ color: '#991B1B' }}
          aria-label="Delete report"
          title="Delete this report"
          data-testid={`report-delete-${r.id}`}
        >
          <TrashIcon size={16} weight="bold" />
        </button>
      </div>

      {/* Title */}
      <div className="mb-1">
        <div
          className="font-display font-extrabold text-2xl leading-tight tracking-tight"
          style={{ color: '#1C2B2B' }}
        >
          {r.eng?.business_name || 'Untitled engagement'}
        </div>
      </div>

      {/* Meta line */}
      <div
        className="text-[11px] uppercase tracking-[0.14em] mb-5"
        style={{ color: '#9CA3AF', fontFamily: 'Inter' }}
      >
        {niceDate} · {niceTime}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-2">
        <a
          href={reportPdfUrl(r.id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 h-9 px-4 text-[12px] font-semibold rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-800 transition-colors active:scale-[0.98]"
          style={{ fontFamily: 'Inter', letterSpacing: '0.04em' }}
          data-testid={`report-pdf-${r.id}`}
        >
          <DownloadSimpleIcon size={14} weight="bold" /> PDF
        </a>
        <Link
          to={`/engagements/${r.eng.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-4 text-[12px] font-semibold rounded-full bg-[#142654] text-white hover:bg-[#2a3e3e] transition-colors active:scale-[0.98] ml-auto"
          style={{ fontFamily: 'Inter', letterSpacing: '0.04em' }}
          data-testid={`report-open-${r.id}`}
        >
          Open engagement <ArrowRightIcon size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}
